import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getLatestPing } from '../utilities/time';

const MAX_ANIMAL_RESULTS = 24;

type SearchButtonProps = {
  groups: Array<any>;
  isMobile: boolean;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  onSelected: (animal: any) => void;
};

function normalizeSearchValue(value: unknown) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();
}

function getSearchRank(query: string, value: unknown) {
  const normalizedValue = normalizeSearchValue(value);
  if (!normalizedValue || !query) {
    return Number.POSITIVE_INFINITY;
  }

  if (normalizedValue === query) {
    return 0;
  }

  if (normalizedValue.startsWith(query)) {
    return 1;
  }

  const wordMatchIndex = normalizedValue
    .split(/\s+/)
    .findIndex((word) => word.startsWith(query));

  if (wordMatchIndex >= 0) {
    return 2 + wordMatchIndex;
  }

  const substringIndex = normalizedValue.indexOf(query);
  if (substringIndex >= 0) {
    return 10 + substringIndex;
  }

  return Number.POSITIVE_INFINITY;
}

function SearchPopup(props: {
  groups: Array<any>;
  isMobile: boolean;
  onSelected: (animal: any) => void;
}) {
  const { groups, isMobile, onSelected } = props;
  const [query, setQuery] = useState('');

  const trackedAnimals = useMemo(
    () => groups
      .flatMap((group) => group.entries)
      .filter((entry) => Array.isArray(entry.individuals) && entry.individuals.length > 0)
      .flatMap((entry) => entry.individuals)
      .sort((left, right) => left.name.localeCompare(right.name)),
    [groups]
  );

  const normalizedQuery = normalizeSearchValue(query);

  const animalResults = useMemo(() => {
    if (!normalizedQuery) {
      return [];
    }

    return trackedAnimals
      .map((animal) => ({
        animal,
        tagRank: normalizeSearchValue(animal.tagId) === normalizedQuery ? 0 : Number.POSITIVE_INFINITY,
        nameRank: getSearchRank(normalizedQuery, animal.name),
        speciesRank: Math.min(
          getSearchRank(normalizedQuery, animal.species),
          getSearchRank(normalizedQuery, animal.scientificName) + 1,
        ),
        latestPing: getLatestPing([animal]),
      }))
      .map((entry) => {
        if (Number.isFinite(entry.tagRank)) {
          return { ...entry, priority: 0, rank: entry.tagRank };
        }

        if (Number.isFinite(entry.nameRank)) {
          return { ...entry, priority: 1, rank: entry.nameRank };
        }

        if (Number.isFinite(entry.speciesRank)) {
          return { ...entry, priority: 2, rank: entry.speciesRank };
        }

        return { ...entry, priority: Number.POSITIVE_INFINITY, rank: Number.POSITIVE_INFINITY };
      })
      .filter(({ priority }) => Number.isFinite(priority))
      .sort((left, right) => {
        if (left.priority !== right.priority) {
          return left.priority - right.priority;
        }

        if (left.rank !== right.rank) {
          return left.rank - right.rank;
        }

        if (left.latestPing !== right.latestPing) {
          return left.latestPing - right.latestPing;
        }

        return left.animal.name.localeCompare(right.animal.name);
      })
      .slice(0, MAX_ANIMAL_RESULTS)
      .map(({ animal }) => animal);
  }, [normalizedQuery, trackedAnimals]);

  const showPrompt = !normalizedQuery;
  const noResults = !showPrompt && animalResults.length === 0;

  return (
    <div className={`species-popup search-popup${isMobile ? ' search-popup--mobile' : ''}`}>
      <div className="search-popup-toolbar">
        <label className="search-popup-input-shell">
          <span className="search-popup-input-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" focusable="false">
              <circle cx="11" cy="11" r="6.5" />
              <path d="M16 16L21 21" />
            </svg>
          </span>
          <input
            autoFocus
            className="search-popup-input"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search name or species"
            aria-label="Search name or species"
          />
        </label>
      </div>

      {noResults && (
        <div className="search-popup-empty-state">
          No matches found.
        </div>
      )}

      {!showPrompt && !noResults && (
        <div className="search-popup-results">
          <ul className="species-list search-popup-list">
            {animalResults.map((animal) => (
              <li
                key={animal.tagId}
                className="species-list-item"
                onClick={() => onSelected(animal)}>
                <div className="species-list-item-details">
                  <span className="species-list-item-title">{animal.name}</span>
                  <span className="species-list-item-meta">{animal.species}</span>
                  <span className="species-list-item-meta">Latest Ping: {getLatestPing([animal])} days ago</span>
                </div>
                <span className="species-list-item-arrow" aria-hidden="true">&gt;</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function SearchButton(props: SearchButtonProps) {
  const { groups, isMobile, isOpen, onOpen, onClose, onSelected } = props;
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    function handleClickOutside(event: MouseEvent) {
      if (ref.current?.contains(event.target as Node)) {
        return;
      }

      onClose();
    }

    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [isOpen, onClose]);

  return (
    <div ref={ref} className="species-button-container species-button-container--search">
      {isOpen && (
        <div className={`popup-container ${isMobile ? 'popup-container--mobile' : 'popup-container--desktop'}`}>
          <SearchPopup
            groups={groups}
            isMobile={isMobile}
            onSelected={(animal) => {
              onSelected(animal);
              onClose();
            }}
          />
        </div>
      )}
      <button
        type="button"
        className="species-button species-button--search"
        aria-label="Search animals"
        onClick={() => {
          if (isOpen) {
            onClose();
            return;
          }

          onOpen();
        }}>
        <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
          <circle cx="11" cy="11" r="6.5" />
          <path d="M16 16L21 21" />
        </svg>
      </button>
    </div>
  );
}
